// ─── Signup attribution ───────────────────────────────────────────────────────
//
// Where a person came from, as PostHog person properties.
//
// public/visitor-ctx.js records the landing before Keycloak takes the browser
// away; this turns those records into the properties identifyUser() sends.
//
// PostHog's own $initial_* properties could not be trusted for this. They are
// read when identify runs, which is after the Keycloak round trip, so:
//   - $initial_referrer was the auth host (api.fastn.dev) for every person who
//     arrived without UTMs, whatever site actually sent them;
//   - a signup that verified its email from a link in a new tab, or landed on
//     a route that redirected, arrived with no UTMs at all.
// Both are overwritten here from the landing record, in the same $identify, so
// PostHog's built-in "initial source" breakdowns read the real source too.
//
//   first_*   $set_once  the first visit that carried a source
//   last_*    $set       the latest one, so a returning person's newest
//                        campaign is visible without losing the first

const FIRST_KEY = "fastn_touch_first";
const LAST_KEY = "fastn_touch_last";
/** Set once this tab has a signed-in session; public/visitor-ctx.js reads it. */
const SIGNED_IN_KEY = "fastn_signed_in";
/** A first touch nobody signed in after for this long is not their source. */
const FIRST_TTL_MS = 90 * 864e5;

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
/** Ad-network click ids. PostHog has an $initial_<id> person property for each. */
const CLICK_IDS = ["gclid", "gbraid", "wbraid", "fbclid", "msclkid", "li_fat_id", "ttclid", "twclid", "rdt_cid"] as const;

export interface Touch {
  params: Record<string, string>;
  referrer: string | null;
  path: string;
  at: string;
}

function parseTouch(raw: string | null): Touch | null {
  if (!raw) return null;
  try {
    const t = JSON.parse(raw) as Partial<Touch>;
    if (!t || typeof t !== "object" || typeof t.at !== "string") return null;
    return {
      params: t.params && typeof t.params === "object" ? t.params : {},
      referrer: typeof t.referrer === "string" ? t.referrer : null,
      path: typeof t.path === "string" ? t.path : "/",
      at: t.at,
    };
  } catch {
    return null;
  }
}

export function readTouches(): { first: Touch | null; last: Touch | null } {
  let first: Touch | null = null;
  let last: Touch | null = null;
  try { first = parseTouch(localStorage.getItem(FIRST_KEY)); } catch { /* storage blocked */ }
  // visitor-ctx.js only replaces an expired first touch when a new sourced
  // visit arrives, so a January touch is still stored at a direct June signup.
  if (first && !(Date.now() - Date.parse(first.at) < FIRST_TTL_MS)) first = null;
  try { last = parseTouch(sessionStorage.getItem(LAST_KEY)); } catch { /* storage blocked */ }
  return { first, last };
}

function clearFirstTouch(): void {
  try { localStorage.removeItem(FIRST_KEY); } catch { /* storage blocked */ }
}

/**
 * The touch identify() sent, kept in memory for the signup request.
 *
 * Set at identify, which runs before the first GET /users/me/memberships: the
 * request that creates the account and stores its source.
 */
let _signupTouch: Touch | null = null;
/** The memberships call has succeeded once in this page: stop sending the header. */
let _delivered = false;

export function rememberSignupTouch(t: Touch | null): void {
  if (t) _signupTouch = t;
}

/**
 * base64url JSON of the signup touch for `x-fastn-signup-source`, or null with
 * nothing to send.
 *
 * Only the first touch: the latest one is a returning visit, and sending it
 * would put the header on every memberships call of the session. The referrer
 * is dropped when it is one of ours, as identify does, so the user record,
 * Slack and PostHog agree.
 */
export function signupSourceHeader(scrubPath: (p: string) => string = (p) => p, hosts: string[] = []): string | null {
  if (_delivered) return null;
  const t = _signupTouch ?? readTouches().first;
  if (!t) return null;
  try {
    const referrer = externalReferrer(t.referrer, hosts)?.url ?? null;
    const bytes = new TextEncoder().encode(JSON.stringify({ ...t, referrer, path: scrubPath(t.path) }));
    let bin = "";
    for (const b of bytes) bin += String.fromCharCode(b);
    return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  } catch {
    return null;
  }
}

/**
 * The memberships call succeeded: the account exists and, if it was new, its
 * source is stored.
 *
 * The first touch is forgotten here rather than at identify. It belongs to the
 * person who signs in after it, and left in place the next person to sign in on
 * this browser would be given it. Clearing it only once the request carrying it
 * succeeded keeps it through a failed request and a reload. This runs whether
 * or not PostHog is on.
 *
 * It also marks the tab as signed in, so visitor-ctx.js stops recording visits
 * that come back from GitHub, Stripe or an OAuth provider as new sources.
 */
export function noteSignedIn(): void {
  _delivered = true;
  _signupTouch = null;
  clearFirstTouch();
  try { sessionStorage.setItem(SIGNED_IN_KEY, "1"); } catch { /* storage blocked */ }
}

export function clearTouches(): void {
  _signupTouch = null;
  _delivered = false;
  clearFirstTouch();
  try {
    sessionStorage.removeItem(LAST_KEY);
    sessionStorage.removeItem(SIGNED_IN_KEY);
  } catch { /* storage blocked */ }
}

/** The referrer, unless it is one of ours: the app itself or the auth host. */
function externalReferrer(referrer: string | null, ownHosts: string[]): { url: string; domain: string } | null {
  if (!referrer) return null;
  try {
    const u = new URL(referrer);
    if (ownHosts.includes(u.host)) return null;
    return { url: referrer, domain: u.hostname };
  } catch {
    return null;
  }
}

function touchProps(prefix: string, t: Touch, ownHosts: string[], scrubPath: (p: string) => string): Record<string, unknown> {
  const ref = externalReferrer(t.referrer, ownHosts);
  const out: Record<string, unknown> = {};
  for (const k of UTM_KEYS) out[`${prefix}${k}`] = t.params[k] ?? null;
  out[`${prefix}referrer`] = ref?.url ?? "$direct";
  out[`${prefix}referring_domain`] = ref?.domain ?? "$direct";
  out[`${prefix}landing_page`] = scrubPath(t.path);
  // The fastn.ai page they landed on, when the website forwarded it.
  out[`${prefix}site_landing_page`] = t.params.landing_page ?? null;
  out[`${prefix}ref`] = t.params.ref ?? null;
  const click = CLICK_IDS.find((k) => t.params[k]);
  out[`${prefix}click_id_type`] = click ?? null;
  out[`${prefix}touch_at`] = t.at;
  return out;
}

/**
 * The person properties for identify().
 *
 * With no first touch on record the visit was direct (or storage was blocked).
 * first_* is still written, as $direct with the rest null: left unset, the
 * person's next tagged visit would $set_once it and first_* would stop meaning
 * the signup source. $initial_referrer is set to $direct too, because otherwise
 * PostHog fills it with the Keycloak host the browser just came back from.
 */
export function attributionPersonProps(opts: {
  first: Touch | null;
  last: Touch | null;
  ownHosts: string[];
  scrubPath?: (p: string) => string;
}): { set: Record<string, unknown>; setOnce: Record<string, unknown> } {
  const scrub = opts.scrubPath ?? ((p: string) => p);
  const setOnce: Record<string, unknown> = {};
  const set: Record<string, unknown> = {};

  if (opts.first) {
    const f = touchProps("first_", opts.first, opts.ownHosts, scrub);
    Object.assign(setOnce, f);
    for (const k of UTM_KEYS) setOnce[`$initial_${k}`] = f[`first_${k}`];
    setOnce.$initial_referrer = f.first_referrer;
    setOnce.$initial_referring_domain = f.first_referring_domain;
    for (const k of CLICK_IDS) if (opts.first.params[k]) setOnce[`$initial_${k}`] = opts.first.params[k];
  } else {
    for (const k of UTM_KEYS) setOnce[`first_${k}`] = null;
    setOnce.first_referrer = "$direct";
    setOnce.first_referring_domain = "$direct";
    setOnce.first_site_landing_page = null;
    setOnce.$initial_referrer = "$direct";
    setOnce.$initial_referring_domain = "$direct";
  }

  if (opts.last) Object.assign(set, touchProps("last_", opts.last, opts.ownHosts, scrub));

  return { set, setOnce };
}

/** Hosts whose referrer is never a source: the app, and Keycloak when it runs elsewhere. */
export function ownHosts(keycloakUrl: string | undefined): string[] {
  const hosts = [window.location.host];
  if (keycloakUrl) {
    try { hosts.push(new URL(keycloakUrl, window.location.href).host); } catch { /* relative or bad: app host already covers it */ }
  }
  return hosts;
}
